import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Progress } from '@/components/ui/progress'
import type { WizardStepConfig } from './useClienteForm'

interface WizardStepperProps {
  steps: WizardStepConfig[]
  currentStep: number
  completedSteps: Set<number>
  onStepClick?: (stepIndex: number) => void
  canGoToStep: (step: number) => boolean
}

export function WizardStepper({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
  canGoToStep,
}: WizardStepperProps) {
  const progress = ((currentStep + 1) / steps.length) * 100

  return (
    <>
      {/* Desktop: Horizontal stepper */}
      <nav className="hidden sm:block" aria-label="Progreso del formulario">
        <ol className="flex items-center justify-between">
          {steps.map((step, index) => {
            const isCompleted = completedSteps.has(index) && index !== currentStep
            const isCurrent = index === currentStep
            const isClickable = canGoToStep(index)

            return (
              <li key={step.id} className="flex items-center flex-1 last:flex-none">
                <button
                  type="button"
                  onClick={() => isClickable && onStepClick?.(index)}
                  disabled={!isClickable}
                  className={cn(
                    'flex items-center gap-2 rounded-lg px-3 py-2 transition-all text-left',
                    isClickable && 'hover:bg-muted cursor-pointer',
                    !isClickable && 'cursor-not-allowed opacity-50'
                  )}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  <div
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-medium transition-colors',
                      isCurrent && 'border-primary bg-primary text-primary-foreground',
                      isCompleted && 'border-primary bg-primary text-primary-foreground',
                      !isCurrent && !isCompleted && 'border-muted-foreground/30 text-muted-foreground'
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <div className="hidden md:block">
                    <p
                      className={cn(
                        'text-sm font-medium',
                        isCurrent && 'text-primary',
                        isCompleted && 'text-foreground',
                        !isCurrent && !isCompleted && 'text-muted-foreground'
                      )}
                    >
                      {step.title}
                    </p>
                  </div>
                </button>

                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      'h-0.5 flex-1 mx-2',
                      completedSteps.has(index) ? 'bg-primary' : 'bg-muted-foreground/20'
                    )}
                  />
                )}
              </li>
            )
          })}
        </ol>
      </nav>

      {/* Mobile: Compact progress */}
      <div className="sm:hidden space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div>
            <span className="font-semibold text-foreground">{steps[currentStep].title}</span>
            <span className="text-muted-foreground ml-1">- {steps[currentStep].description}</span>
          </div>
          <span className="text-muted-foreground shrink-0">
            {currentStep + 1} de {steps.length}
          </span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>
    </>
  )
}
